import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { notification } from 'antd';
import { signIn, startSession, saveTokenToLocal } from '../services';
import { ErrorMessage, getErrorMessage, getErrorCodeAndStatus } from 'utils/axios';
import { NotificationProps } from 'utils/types';
import { auth } from '../firebaseConfig';
import { LOGIN, ROOT } from 'configs/routeNames';
import { clearTempCredentials, CONTRACT_EMAIL_KEY } from '../SignUp/utils/formUtils';
import { SetStateAction } from 'react';
import { removeFromLocal } from 'utils/cache';

interface UseAuthenticationOptions {
  redirectPath?: string;
  setIsAuthenticating?: (value: SetStateAction<boolean>) => void;
}

export const useAuthentication = (options: UseAuthenticationOptions = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const startSessionMutation = useMutation({
    mutationKey: ['startSession'],
    mutationFn: (isSSO: boolean = false) => startSession(isSSO),
    onSuccess: async () => {
      const from = location.state?.from?.pathname;
      const search = location.state?.from?.search || '';
      const redirectTo = options.redirectPath || (from ? from + search : ROOT);
      navigate(redirectTo, { replace: true });
    },
    onError: (error: ErrorMessage) => {
      notification.error({
        message: getErrorMessage(error),
        ...NotificationProps,
      });
    },
  });

  const signInMutation = useMutation({
    mutationKey: ['sign-in'],
    mutationFn: signIn,
    onMutate: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'user-info'] });
    },
    onSuccess: async ({ idToken }) => {
      try {
        saveTokenToLocal({ accessToken: idToken });
        await startSessionMutation.mutateAsync(false);
      } finally {
        options.setIsAuthenticating?.(false);
      }
    },
    onError: async (error) => {
      options.setIsAuthenticating?.(false);

      const { code, status } = getErrorCodeAndStatus(error);
      if (status !== 401 && code !== 'ERR_BAD_REQUEST') {
        notification.error({
          message: error.message,
          ...NotificationProps,
        });
      }
      await auth.signOut();
      localStorage.clear();
    },
  });

  const performAutoLogin = async (email: string, password: string) => {
    try {
      const { idToken } = await signInMutation.mutateAsync({ email, password });
      saveTokenToLocal({ accessToken: idToken });
      await startSessionMutation.mutateAsync(false);
      clearTempCredentials();
      return { success: true };
    } catch (error) {
      // fallback: if something goes wrong, redirect to login page
      options.setIsAuthenticating?.(false);
      navigate(`${ROOT}${LOGIN}`);
      await auth.signOut();
      clearTempCredentials();
      removeFromLocal(CONTRACT_EMAIL_KEY);
      localStorage.clear();
    }
  };

  return {
    startSession: startSessionMutation,
    signIn: signInMutation,
    performAutoLogin,
    isLoading: signInMutation.isPending || startSessionMutation.isPending,
    isSigningIn: signInMutation.isPending,
    isStartingSession: startSessionMutation.isPending,
    error: signInMutation.error || startSessionMutation.error,
    signInError: signInMutation.error,
    sessionError: startSessionMutation.error,
  };
};
